"use client";

import type React from "react";

import { useState, useRef, useEffect } from "react";
import {
  Check,
  Copy,
  FileText,
  Hash,
  Heart,
  LinkIcon,
  Send,
  Share2,
  Sparkles,
  Tag,
  Users,
  MessageSquareHeart,
  Linkedin,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";

import { Button } from "@/shadcnComponents/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shadcnComponents/ui/card";
import { Input } from "@/shadcnComponents/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shadcnComponents/ui/tabs";
import { useToast } from "@/shadcnComponents/ui/use-toast";
import { useAuth } from "@/Providers/Auth";
import {
  copyToClipboard,
  publishOnLinkedIn,
  shareContent,
  shareOnWhatsApp,
} from "@/Helpers/helper";
import { useInfluencer } from "@/Providers/Influencer";
import MobileMenuBar from "@/Components/Menubar/MobileMenuBar";
import {
  brandTemplate1,
  brandTemplate2,
  brandTemplate3,
  brandTemplate4,
  influencerTemplate1,
  influencerTemplate2,
  influencerTemplate3,
  influencerTemplate4,
} from "./HelpUsTemplates";

export default function HelpUsPage() {
  const { toast } = useToast();
  const authState = useAuth();
  const influencerState = useInfluencer();
  const [copied, setCopied] = useState(false);
  const [postLink, setPostLink] = useState("");
  const [, setPostType] = useState("influencer");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const referralLink = `${window.location.protocol}//${window.location.host}/influencer/?refer=${authState?.loggedUser?.referralCode}`;
  const linkRef = useRef<HTMLInputElement>(null);

  // Add state to track which template is displayed (now supporting 4 templates)
  const [templateVersion, setTemplateVersion] = useState(1);

  // Add state to track which brand template is displayed
  const [brandTemplateVersion, setBrandTemplateVersion] = useState(1);

  useEffect(() => {
    if (authState?.loggedUser?.contributionLink) {
      setSubmitted(true);
    }
  }, [authState]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    //match regex of a linkedin post link
    const postLinkRegex =
      /https?:\/\/(www\.)?linkedin\.com\/(?:feed\/update\/|posts\/|activity\/)[\w-]+\/?/i;
    if (!postLinkRegex.test(postLink)) {
      setIsSubmitting(false);
      toast({
        title: "Invalid link!",
        description: "Please provide a valid LinkedIn post link.",
      });
      return;
    }

    if (postLink) {
      const result = await influencerState?.updatePersonalInfo({
        contributionLink: postLink,
      });
      if (result?.success) {
        setIsSubmitting(false);
        setSubmitted(true);
        toast({
          title: "Thank you!",
          description: "Your contribution has been received. You're awesome!",
        });
      } else {
        setIsSubmitting(false);
        toast({
          title: "Failed to update!",
          description: "Please try again.",
        });
      }
    } else {
      setIsSubmitting(false);
      toast({
        title: "Please enter a link!",
        description: "You must provide a valid link",
      });
    }
  };

  // Function to cycle through templates
  const toggleTemplate = (direction: "next" | "prev" = "next") => {
    if (direction === "next") {
      setTemplateVersion((prev) => (prev === 4 ? 1 : prev + 1));
    } else {
      setTemplateVersion((prev) => (prev === 1 ? 4 : prev - 1));
    }
  };

  // Function to cycle through brand templates
  const toggleBrandTemplate = (direction: "next" | "prev" = "next") => {
    if (direction === "next") {
      setBrandTemplateVersion((prev) => (prev === 4 ? 1 : prev + 1));
    } else {
      setBrandTemplateVersion((prev) => (prev === 1 ? 4 : prev - 1));
    }
  };

  // Get the current template based on state
  const getCurrentTemplate = () => {
    switch (templateVersion) {
      case 1:
        return influencerTemplate1;
      case 2:
        return influencerTemplate2;
      case 3:
        return influencerTemplate3;
      case 4:
        return influencerTemplate4;
      default:
        return influencerTemplate1;
    }
  };

  // Get the current brand template based on state
  const getCurrentBrandTemplate = () => {
    switch (brandTemplateVersion) {
      case 1:
        return brandTemplate1;
      case 2:
        return brandTemplate2;
      case 3:
        return brandTemplate3;
      case 4:
        return brandTemplate4;
      default:
        return brandTemplate1;
    }
  };

  // Function to format template text for display
  const formatTemplateText = (text: string) => {
    // Add hashtags to the end of each template
    const textWithHashtags = `${text}\n\n#proudlyhelpinganchors #anchors`;

    return textWithHashtags.split("\n\n").map((paragraph, i) => (
      <p key={i} className="mb-2">
        {paragraph}
      </p>
    ));
  };

  const publishCreatorOnLinkedIn = () => {
    // Get the current template with hashtags and referral link
    const shareText = `${getCurrentTemplate()}\n\n#anchors #proudlyhelpinganchors @RaviAhirwar @Aesha\n\n${referralLink}`;
    publishOnLinkedIn(shareText);
  };

  const publishBrandOnLinkedIn = () => {
    // Get the current brand template with hashtags and referral link
    const shareText = `${getCurrentBrandTemplate()}\n\n#anchors #contentcreation #impactfulmarketing @RaviAhirwar @Aesha\n\n${referralLink}`;
    publishOnLinkedIn(shareText);
  };
  return (
    <>
      <MobileMenuBar />

      <div className="container mx-auto max-w-4xl p-4 md:p-4">
        {/* Header */}
        <div className="mb-10 text-center">
          <h1 className="mb-3 bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 bg-clip-text pb-1 text-3xl font-bold text-transparent md:text-4xl">
            Help Us Grow
          </h1>
          <p className="mx-auto max-w-2xl text-zinc-400 text-sm">
            Your influence has more power than you know. With your voice, we can
            build something that truly puts creators first. The best part?
            Everything that comes through this growth goes back to creators like
            you.
          </p>
        </div>

        {/* Main Content with consistent width */}
        <div className="flex flex-col items-center">
          {/* Main Tabs */}
          <Tabs defaultValue="referral" className="w-full md:w-[48%]">
            <div className="mb-6">
              <TabsList className="grid w-full grid-cols-2 bg-zinc-800/70 p-1">
                <TabsTrigger
                  value="referral"
                  className="relative flex items-center gap-2 py-1.5 text-xs overflow-hidden rounded-sm data-[state=active]:bg-violet-600/80"
                >
                  <Users className="h-3.5 w-3.5" />
                  <span>Invite friends</span>
                </TabsTrigger>
                <TabsTrigger
                  value="write"
                  className="relative flex items-center gap-2 py-1.5 text-xs overflow-hidden rounded-sm data-[state=active]:bg-fuchsia-600/80"
                >
                  <MessageSquareHeart className="h-3.5 w-3.5" />
                  <span>Write About Us</span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Referral Tab Content */}
            <TabsContent value="referral" className="mt-0 w-full">
              <Card className="overflow-hidden border-zinc-800 bg-zinc-900/50 shadow-lg shadow-purple-900/10">
                <CardHeader>
                  <div className="flex items-center justify-center gap-2">
                    <Users className="h-5 w-5 text-violet-400" />
                    <CardTitle className="text-white text-lg">
                      Share Your Referral Link
                    </CardTitle>
                  </div>
                  <CardDescription className="text-center text-xs">
                    Invite. Empower. Grow the creator community and earn perks.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-lg bg-zinc-800/50 p-4 text-center">
                    <p className="mb-3 text-xs text-zinc-300">
                      Share your unique link with fellow creators and help them
                      discover anchors. Every signup makes our community
                      stronger and earns you rewards in return.
                    </p>
                    <div className="flex gap-2">
                      <div className="relative flex-grow">
                        <Input
                          ref={linkRef}
                          value={referralLink}
                          readOnly
                          className="border-zinc-700 bg-zinc-800 pr-10 text-zinc-300 focus-visible:ring-violet-500 text-xs h-8 py-1"
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="absolute right-0 top-0 h-full w-8 rounded-l-none text-zinc-400 hover:bg-zinc-700 hover:text-white p-0"
                          onClick={() =>
                            copyToClipboard({
                              text: `${referralLink}`,
                              onSuccess: () => {
                                setCopied(true);
                                toast({
                                  title: "Copied to clipboard!",
                                  description:
                                    "Your referral link has been copied.",
                                });
                              },
                            })
                          }
                        >
                          {copied ? (
                            <Check className="h-3.5 w-3.5" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          onClick={() =>
                            shareContent({
                              text: `Join me on anchors, a platform that connects influencers with top brands! Use my referral link: ${referralLink}`,
                            })
                          }
                          variant="default"
                          size="icon"
                          className="bg-violet-600 hover:bg-violet-500 h-8 w-8 p-0"
                        >
                          <Share2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="default"
                          size="icon"
                          className="bg-green-600 hover:bg-green-500 h-8 w-8 p-0"
                          onClick={() =>
                            shareOnWhatsApp({
                              text: `Join me on anchors, a platform that connects influencers with top brands! Use my referral link: ${referralLink}`,
                            })
                          }
                        >
                          <svg
                            viewBox="0 0 24 24"
                            className="h-3.5 w-3.5 fill-white"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M17.6 6.32A7.85 7.85 0 0 0 12.05 4c-4.38 0-7.93 3.55-7.93 7.93a7.9 7.9 0 0 0 1.07 3.98L4 20l4.17-1.1a7.9 7.9 0 0 0 3.78.96h.01c4.38 0 7.93-3.55 7.93-7.93 0-2.12-.82-4.1-2.3-5.6zm-5.55 12.18h-.01a6.56 6.56 0 0 1-3.35-.92l-.24-.14-2.5.65.67-2.43-.16-.25a6.57 6.57 0 0 1-1-3.47c0-3.64 2.96-6.6 6.6-6.6a6.56 6.56 0 0 1 6.6 6.6c0 3.64-2.96 6.56-6.6 6.56zm3.62-4.93c-.2-.1-1.17-.58-1.35-.64-.18-.07-.32-.1-.45.1-.13.2-.5.64-.62.77-.11.13-.23.15-.43.05-.2-.1-.84-.31-1.6-.99-.6-.53-.99-1.18-1.1-1.38-.12-.2-.01-.3.09-.4.09-.09.2-.23.3-.35.1-.12.13-.2.2-.34.07-.13.03-.25-.02-.35-.05-.1-.45-1.08-.62-1.48-.16-.39-.33-.33-.45-.34-.11-.01-.25-.01-.38-.01-.13 0-.35.05-.53.25-.18.2-.7.68-.7 1.67 0 .98.72 1.94.82 2.07.1.13 1.4 2.14 3.4 3 .47.2.84.33 1.13.42.48.15.91.13 1.25.08.38-.06 1.17-.48 1.33-.94.17-.46.17-.86.12-.94-.05-.08-.19-.13-.4-.23z" />
                          </svg>
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-md border border-violet-900/50 bg-violet-950/20 p-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Sparkles className="h-4 w-4 text-violet-400" />
                      <p className="text-xs font-semibold text-violet-300">
                        Referral Benefits
                      </p>
                    </div>
                    <ul className="mt-2 space-y-1 text-xs text-zinc-400">
                      <li>• Earn rewards for each successful signup</li>
                      <li>• Get priority access to new features</li>
                      <li>• Boost your influence score on the platform</li>
                    </ul>
                  </div>
                </CardContent>
                <CardFooter className="border-t border-zinc-800 bg-zinc-900/80 px-4 py-3">
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <Sparkles className="h-3.5 w-3.5 text-violet-400" />
                    <p>
                      Every referral adds another voice to the creator movement
                      we're building, together.
                    </p>
                  </div>
                </CardFooter>
              </Card>
            </TabsContent>

            {/* Write About Us Tab Content */}
            <TabsContent value="write" className="mt-0 w-full">
              <Card className="overflow-hidden border-zinc-800 bg-zinc-900/50 shadow-lg shadow-fuchsia-900/10">
                <CardHeader className="px-3 md:px-6 py-3 md:py-4">
                  <div className="flex items-center justify-center gap-1 md:gap-2">
                    <MessageSquareHeart className="h-4 w-4 md:h-5 md:w-5 text-fuchsia-400" />
                    <CardTitle className="text-white text-base md:text-lg">
                      Why your support matters
                    </CardTitle>
                  </div>
                  <CardDescription className="text-center text-[10px] md:text-xs">
                    You're not just backing a platform. You're helping build a
                    fairer, kinder creator economy where no creator walks alone.
                  </CardDescription>
                  <ul className="mt-1 md:mt-2 space-y-0.5 md:space-y-1 text-[10px] md:text-xs text-zinc-400">
                    <li>
                      Every shout out brings this movement to life. It means
                      more creators joining, more brands discovering us, and
                      more paid collabs for all.
                    </li>
                    <li>Your voice could be someone else's breakthrough.</li>
                  </ul>
                </CardHeader>
                <CardContent className="space-y-4">
                  {submitted ? (
                    <div className="flex flex-col items-center justify-center rounded-lg bg-zinc-800/50 p-8 text-center">
                      <div className="flex items-center justify-center mb-2">
                        <div className="rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 p-3">
                          <Heart className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <h3 className="mb-1 text-lg font-semibold text-white">
                        Thank You!
                      </h3>
                      <p className="text-sm text-zinc-400">
                        Your support means the world to us. Together, we're
                        building something amazing!
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="rounded-lg bg-zinc-800/50 p-4 text-center">
                        <h3 className="mb-3 text-xs font-medium text-white">
                          How to write about us:
                        </h3>
                        <ul className="space-y-2 text-xs text-zinc-300 inline-block text-left">
                          <li className="flex items-start gap-2">
                            <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
                            <span>
                              Write a LinkedIn post about anchors (focusing on
                              either influencers or brands)
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Hash className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
                            <span>
                              Use hashtags-{" "}
                              <span className="font-semibold text-amber-300">
                                #anchors #proudlyhelpinganchors
                              </span>
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Tag className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
                            <span>
                              Tag our co-founders:{" "}
                              <span className="font-semibold text-amber-300">
                                Ravi and Aesha
                              </span>
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Send className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
                            <span>
                              Paste your post link below to be part of the
                              growth
                            </span>
                          </li>
                        </ul>
                      </div>

                      <Tabs
                        defaultValue="influencer"
                        onValueChange={setPostType}
                        className="w-full"
                      >
                        <TabsList className="grid w-full grid-cols-2 bg-zinc-800">
                          <TabsTrigger
                            value="influencer"
                            className="relative overflow-hidden data-[state=active]:text-[#a3a3a3] data-[state=active]:bg-fuchsia-900/30 text-xs py-1"
                          >
                            Influencer Focus
                          </TabsTrigger>
                          <TabsTrigger
                            value="brand"
                            className="relative overflow-hidden data-[state=active]:text-[#a3a3a3] data-[state=active]:bg-fuchsia-900/30 text-xs py-1"
                          >
                            Brand Focus
                          </TabsTrigger>
                        </TabsList>
                        <TabsContent value="influencer" className="mt-2">
                          <div className="space-y-2 md:space-y-3">
                            {/* Template Card with auto-height to show all content */}
                            <div className="border border-zinc-700 rounded-md bg-zinc-800 shadow-inner">
                              <div className="p-2 md:p-3 pb-1 border-b border-zinc-700 flex items-center justify-between">
                                <p className="text-[8px] md:text-[10px] font-medium text-zinc-400">
                                  Template {templateVersion} of 4
                                </p>
                                <div className="flex gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-4 w-4 md:h-5 md:w-5 rounded-full p-0 text-zinc-400 hover:bg-zinc-700 hover:text-white"
                                    onClick={() => toggleTemplate("prev")}
                                  >
                                    <ArrowLeft className="h-2 w-2 md:h-2.5 md:w-2.5" />
                                    <span className="sr-only">
                                      Previous template
                                    </span>
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-4 w-4 md:h-5 md:w-5 rounded-full p-0 text-zinc-400 hover:bg-zinc-700 hover:text-white"
                                    onClick={() => toggleTemplate("next")}
                                  >
                                    <ArrowRight className="h-2 w-2 md:h-2.5 md:w-2.5" />
                                    <span className="sr-only">
                                      Next template
                                    </span>
                                  </Button>
                                </div>
                              </div>
                              <div className="p-2 md:p-3 pt-1 md:pt-2">
                                <div className="text-[8px] md:text-[10px] text-zinc-300 leading-relaxed">
                                  {formatTemplateText(getCurrentTemplate())}
                                </div>
                              </div>
                            </div>

                            <Button
                              type="button"
                              onClick={publishCreatorOnLinkedIn}
                              className="w-full bg-[#0A66C2] hover:bg-[#0958A7] text-white text-[10px] md:text-xs py-1 md:py-1.5 h-7 md:h-8 flex items-center justify-center"
                            >
                              <Linkedin className="mr-1.5 md:mr-2 h-3 w-3 md:h-3.5 md:w-3.5" />
                              Publish on LinkedIn
                            </Button>
                          </div>
                        </TabsContent>
                        <TabsContent value="brand" className="mt-2">
                          <div className="space-y-2 md:space-y-3">
                            {/* Brand Template Card with auto-height to show all content */}
                            <div className="border border-zinc-700 rounded-md bg-zinc-800 shadow-inner">
                              <div className="p-2 md:p-3 pb-1 border-b border-zinc-700 flex items-center justify-between">
                                <p className="text-[8px] md:text-[10px] font-medium text-zinc-400">
                                  Template {brandTemplateVersion} of 4
                                </p>
                                <div className="flex gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-4 w-4 md:h-5 md:w-5 rounded-full p-0 text-zinc-400 hover:bg-zinc-700 hover:text-white"
                                    onClick={() => toggleBrandTemplate("prev")}
                                  >
                                    <ArrowLeft className="h-2 w-2 md:h-2.5 md:w-2.5" />
                                    <span className="sr-only">
                                      Previous template
                                    </span>
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-4 w-4 md:h-5 md:w-5 rounded-full p-0 text-zinc-400 hover:bg-zinc-700 hover:text-white"
                                    onClick={() => toggleBrandTemplate("next")}
                                  >
                                    <ArrowRight className="h-2 w-2 md:h-2.5 md:w-2.5" />
                                    <span className="sr-only">
                                      Next template
                                    </span>
                                  </Button>
                                </div>
                              </div>
                              <div className="p-2 md:p-3 pt-1 md:pt-2">
                                <div className="text-[8px] md:text-[10px] text-zinc-300 leading-relaxed">
                                  {formatTemplateText(
                                    getCurrentBrandTemplate()
                                  )}
                                </div>
                              </div>
                            </div>

                            <Button
                              type="button"
                              onClick={publishBrandOnLinkedIn}
                              className="w-full bg-[#0A66C2] hover:bg-[#0958A7] text-white text-[10px] md:text-xs py-1 md:py-1.5 h-7 md:h-8 flex items-center justify-center"
                            >
                              <Linkedin className="mr-1.5 md:mr-2 h-3 w-3 md:h-3.5 md:w-3.5" />
                              Publish on LinkedIn
                            </Button>
                          </div>
                        </TabsContent>
                      </Tabs>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <LinkIcon className="h-4 w-4 text-zinc-400" />
                          <p className="text-sm text-zinc-300">
                            Your Post Link
                          </p>
                        </div>
                        <Input
                          placeholder="https://linkedin.com/posts/your-post-link"
                          value={postLink}
                          onChange={(e) => setPostLink(e.target.value)}
                          className="border-zinc-700 bg-zinc-800 text-zinc-300 focus-visible:ring-fuchsia-500 text-xs h-8 py-1"
                          required
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white hover:from-fuchsia-500 hover:to-pink-500 text-xs py-2"
                      >
                        {isSubmitting
                          ? "Submitting.. "
                          : "Submit Your Contribution"}
                      </Button>
                    </form>
                  )}
                </CardContent>
                <CardFooter className="border-t border-zinc-800 bg-zinc-900/80 px-4 py-3">
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <Sparkles className="h-3.5 w-3.5 flex-shrink-0 text-fuchsia-400" />
                    <p>
                      Feel proud - you're helping build a space where creators
                      can truly belong.
                    </p>
                  </div>
                </CardFooter>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Bottom Inspiration - aligned with cards */}
          <div className="mt-10 w-full md:w-[48%] rounded-xl bg-gradient-to-r from-violet-950/30 via-fuchsia-950/30 to-pink-950/30 p-6 text-center">
            <h3 className="mb-2 text-sm font-semibold text-white">
              Together, Let'sBuild Something Bigger!
            </h3>
            <p className="mx-auto text-xs text-zinc-400">
              Each referral and every shout out helps us reach more creators,
              shape better opportunities, and make this space truly ours. Your
              support today is shaping the future of the creator economy.
            </p>
          </div>

          {/* Footer message */}
          <div className="mt-8 mb-4 w-full md:w-[48%] flex items-start space-x-2 text-left">
            <Heart className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-pink-500 fill-pink-500" />
            <p className="text-xs font-light text-zinc-400">
              Crafted with love, for creators like you-because your voice
              deserves to be heard.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
